import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass, SmallTitleSpan } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { WMS1101_R01DetailDto } from './Dto_WMS1101_R01';
import { useWMS1101_R01Store } from './Store_WMS1101_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'WMS1101_R01-detail-grid-column-state';

interface Props {
	detailGridRef: React.RefObject<AgGridReact<WMS1101_R01DetailDto> | null>;
	defaultWMS1101_R01DetailRow: WMS1101_R01DetailDto;
}

export const DetailTable_WMS1101_R01 = (props: Props) => {
	const { detailGridRef, defaultWMS1101_R01DetailRow } = props;

	const {
		WMS1101_R01DetailList, setWMS1101_R01DetailList,
		selectedWMS1101_R01DetailList, setSelectedWMS1101_R01DetailList, initSelectedWMS1101_R01DetailList,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useWMS1101_R01Store();

	const { unitList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: detailGridRef,
		key: 'valueIndex',
		gridData: WMS1101_R01DetailList,
		defaultGridData: defaultWMS1101_R01DetailRow,
		selectedRow: selectedWMS1101_R01DetailList,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		unit: '단위',
		qty: '주문량',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

	const baseColumnDefs: ColDef<WMS1101_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 80,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'locationNm'),
		},
		{
			headerName: '단위', colId: 'unit', field: 'unit', width: 50,
			cellStyle: (p) => getCellStyle(p, 'unit'),
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: '주문량', colId: 'qty', field: 'qty', width: 70,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			cellStyle: (p) => getCellStyle(p, 'qty'),
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 150,
			editable: true,
			cellStyle: (p) => getCellStyle(p, 'remark'),
		},
	], [headerNames, unitList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS1101_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS1101_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS1101_R01DetailDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedWMS1101_R01DetailList.includes(rowData['valueIndex'] as any);
		if (isSelected) {
			setSelectedWMS1101_R01DetailList([]);
		} else {
			setSelectedWMS1101_R01DetailList([rowData['valueIndex']] as any);
		}
	}, [selectedWMS1101_R01DetailList, setSelectedWMS1101_R01DetailList]);

	const pinnedBottomRow = useMemo(() => ([{
		itemCd: `${WMS1101_R01DetailList.length.toLocaleString()}건`,
	}]), [WMS1101_R01DetailList]);

	return (
		<div className="flex flex-1 pb-[12px] gap-[4px] h-full min-h-full">
			<div className="w-full pb-[12px]">
				<SmallTitleSpan> 입고 목록 </SmallTitleSpan>
				<GridContextWrapper<WMS1101_R01DetailDto>
					enableCellCalculation
					gridWrapperRef={gridWrapperRef}
					gridRef={detailGridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setWMS1101_R01DetailList}
					initSelected={initSelectedWMS1101_R01DetailList}
					defaultRowData={defaultWMS1101_R01DetailRow}
					baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setDetailColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<WMS1101_R01DetailDto>
						ref={detailGridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<WMS1101_R01DetailDto>({
							idKey: 'valueIndex',
							selectedList: selectedWMS1101_R01DetailList,
							deletedList: [],
						})}
						onRowClicked={handleRowClick}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRow}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={COLUMN_STATE_KEY}
						setColumnState={setDetailColumnState}
						scrollTop={detailScrollTop}
						setScrollTop={setDetailScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
